/* eslint-disable react/prop-types */
// Piezas comunes de las fichas y listas de leads y clientes (C3D-46).
import { useState } from "react";
import { ArrowLeft, Globe, Mail, MessageCircle, Phone } from "lucide-react";
import getMediaUrl from "../tools/utils.jsx";
import { initials, whatsappDigits } from "./crmUtils.js";

/** Logo o iniciales. `size`: "sm" (listas) o "lg" (cabecera de la ficha). */
export function CrmAvatar({ name, imageUrl, size = "sm" }) {
    const [failed, setFailed] = useState(false);
    const box = size === "lg" ? "w-16 h-16 text-xl" : "w-11 h-11 text-sm";
    if (imageUrl && !failed) {
        return (
            <img
                src={getMediaUrl(imageUrl)}
                alt=""
                onError={() => setFailed(true)}
                className={`${box} shrink-0 rounded-xl object-contain bg-base-200 border border-base-content/10 p-1`}
            />
        );
    }
    return (
        <span className={`${box} shrink-0 rounded-xl bg-base-200 border border-base-content/10 flex items-center justify-center font-semibold text-base-content/70`} aria-hidden="true">
            {initials(name)}
        </span>
    );
}

/** Bloque de la ficha con título y, opcionalmente, una acción a la derecha. */
export function CrmSection({ title, action = null, children, className = "" }) {
    return (
        <section className={`rounded-xl border border-base-content/10 bg-base-100 p-4 flex flex-col gap-4 ${className}`}>
            <div className="flex items-center justify-between gap-3 min-h-8">
                <h2 className="font-semibold text-base-content">{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

/** Dato en modo lectura: etiqueta arriba y valor, o «Sin indicar». */
export function CrmField({ label, children, className = "" }) {
    const empty = children === null || children === undefined || children === "";
    return (
        <div className={`flex flex-col gap-0.5 min-w-0 ${className}`}>
            <dt className="text-xs font-medium text-base-content/60">{label}</dt>
            <dd className={`text-sm break-words ${empty ? "text-base-content/40" : "text-base-content"}`}>
                {empty ? "Sin indicar" : children}
            </dd>
        </div>
    );
}

/** Campo de formulario con la etiqueta encima y el error debajo. */
export function CrmInput({ label, error, help, children, className = "" }) {
    return (
        <label className={`form-control gap-1 ${className}`}>
            <span className="label-text text-sm font-medium">{label}</span>
            {children}
            {help && !error && <span className="text-xs text-base-content/60">{help}</span>}
            {error && <span className="text-xs text-error">{Array.isArray(error) ? error[0] : String(error)}</span>}
        </label>
    );
}

/** Accesos de contacto: WhatsApp, llamar, email y web, solo los que haya. */
export function ContactActions({ phone, email, website, size = "sm" }) {
    const digits = whatsappDigits(phone);
    const btn = `btn btn-${size} gap-1.5 whitespace-nowrap`;
    if (!digits && !email && !website) return null;
    return (
        <div className="flex flex-wrap gap-2">
            {digits && (
                <a className={`${btn} btn-primary`} href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4" aria-hidden="true" /> WhatsApp
                </a>
            )}
            {phone && (
                <a className={`${btn} btn-outline`} href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}>
                    <Phone className="w-4 h-4" aria-hidden="true" /> Llamar
                </a>
            )}
            {email && (
                <a className={`${btn} btn-outline`} href={`mailto:${email}`}>
                    <Mail className="w-4 h-4" aria-hidden="true" /> Email
                </a>
            )}
            {website && (
                <a className={`${btn} btn-ghost`} href={website} target="_blank" rel="noopener noreferrer">
                    <Globe className="w-4 h-4" aria-hidden="true" /> Web
                </a>
            )}
        </div>
    );
}

/** Botón para volver a la lista. */
export function BackButton({ label, onClick }) {
    return (
        <button type="button" className="btn btn-ghost btn-sm gap-2 self-start" onClick={onClick}>
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {label}
        </button>
    );
}

/**
 * Barra de Guardar y Cancelar mientras se edita. Va pegada abajo para que se
 * vea aunque el formulario sea largo.
 */
export function EditBar({ saving, dirty, onCancel, onSave, saveLabel = "Guardar" }) {
    return (
        <div className="sticky bottom-0 z-10 -mx-2 sm:mx-0 rounded-xl border border-base-content/10 bg-base-100/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
            <span className="text-sm text-base-content/70">{dirty ? "Hay cambios sin guardar" : "Sin cambios"}</span>
            <div className="flex gap-2">
                <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>Cancelar</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving || !dirty}>
                    {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                    {saveLabel}
                </button>
            </div>
        </div>
    );
}

/** Tarjeta de la lista: avatar, nombre, debajo lo secundario y las insignias. */
export function CrmListCard({ title, subtitle, imageUrl, badges, meta, onOpen, actions = null }) {
    return (
        <article className="group flex flex-col gap-3 rounded-xl border border-base-content/10 bg-base-100 p-4 hover:border-primary/50 transition-colors">
            <button
                type="button"
                onClick={onOpen}
                className="flex items-start gap-3 text-left min-w-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary rounded-lg"
            >
                <CrmAvatar name={title} imageUrl={imageUrl} />
                <span className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-semibold leading-snug text-base-content group-hover:text-primary truncate">{title}</span>
                    {subtitle && <span className="text-sm text-base-content/70 truncate">{subtitle}</span>}
                </span>
            </button>
            {badges && <div className="flex flex-wrap gap-1.5">{badges}</div>}
            {meta && <p className="text-xs text-base-content/60">{meta}</p>}
            {actions && <div className="mt-auto pt-1">{actions}</div>}
        </article>
    );
}

/** Esqueleto de la lista mientras carga. */
export function CrmListSkeleton() {
    return (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true" aria-label="Cargando">
            {[0, 1, 2, 3, 4, 5].map((key) => <div key={key} className="skeleton h-36 rounded-xl" />)}
        </div>
    );
}

/** Esqueleto de la ficha mientras carga. */
export function CrmDetailSkeleton() {
    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4" aria-busy="true" aria-label="Cargando ficha">
            <div className="skeleton h-24 rounded-xl" />
            <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
                <div className="skeleton h-96 rounded-xl" />
                <div className="skeleton h-72 rounded-xl" />
            </div>
        </div>
    );
}
